import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import { parseDossierIdFromPath, type QuickCreateKind, type QuickCreateTile } from '../../lib/quickCreate';
import { DossierCreateModal } from '../dossiers/DossierCreateModal';
import { ContactEditModal } from '../contacts/ContactEditModal';
import { NewPolarQuotePanel } from '../billing/NewPolarQuotePanel';
import { FaIcon } from './FaIcon';
import { AppointmentDialog } from '../calendar/AppointmentDialog';
import {
  QuickCreateAudienceDialog,
  QuickCreateTaskDialog,
  QuickCreateTimeEntryDialog,
} from './QuickCreateDialogs';

interface Props {
  tiles: QuickCreateTile[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  followUp: QuickCreateKind | null;
  onFollowUp: (kind: QuickCreateKind | null) => void;
}

export function QuickCreateHost({
  tiles,
  open,
  onOpenChange,
  followUp,
  onFollowUp,
}: Props) {
  const { pathname } = useLocation();
  const contextDossierId = parseDossierIdFromPath(pathname);

  useEffect(() => {
    onFollowUp(null);
  }, [pathname, onFollowUp]);

  function pick(kind: QuickCreateKind) {
    onOpenChange(false);
    onFollowUp(kind);
  }

  function closeFollowUp() {
    onFollowUp(null);
  }

  return (
    <>
      {open ? (
        <div
          className="fixed inset-0 bg-black/40 z-50 p-4 flex items-start justify-center pt-[15vh]"
          onClick={() => onOpenChange(false)}
        >
          <div
            className="w-full max-w-lg bg-white rounded-cime shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
              <h2 className="font-semibold text-cime-text-primary">Créer</h2>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="text-neutral-400 hover:text-neutral-700"
                aria-label="Fermer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-3 p-5">
              {tiles.map((tile) => (
                <button
                  key={tile.kind}
                  type="button"
                  onClick={() => pick(tile.kind)}
                  className="flex flex-col items-center gap-2 rounded-cime border border-neutral-200 px-3 py-4 text-sm text-cime-text-primary hover:border-cime-primary/40 hover:bg-neutral-50 transition-colors"
                >
                  <FaIcon name={tile.icon} className="w-6 h-6 opacity-80" />
                  <span>{tile.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {followUp === 'dossier' ? (
        <DossierCreateModal onClose={closeFollowUp} />
      ) : null}
      {followUp === 'contact' ? (
        <ContactEditModal
          onClose={closeFollowUp}
          onSaved={() => toast.success('Contact créé')}
        />
      ) : null}
      {followUp === 'audience' ? (
        <QuickCreateAudienceDialog contextDossierId={contextDossierId} onClose={closeFollowUp} />
      ) : null}
      {followUp === 'appointment' ? (
        <AppointmentDialog
          open
          appointmentId={null}
          contextDossierId={contextDossierId}
          onClose={closeFollowUp}
        />
      ) : null}
      {followUp === 'task' ? (
        <QuickCreateTaskDialog contextDossierId={contextDossierId} onClose={closeFollowUp} />
      ) : null}
      {followUp === 'timeEntry' ? (
        <QuickCreateTimeEntryDialog contextDossierId={contextDossierId} onClose={closeFollowUp} />
      ) : null}
      {followUp === 'invoice' ? (
        <div
          className="fixed inset-0 bg-black/40 z-50 overflow-y-auto p-4"
          onClick={closeFollowUp}
        >
          <div
            className="mx-auto my-8 w-full max-w-5xl rounded-cime bg-cime-bg p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <NewPolarQuotePanel
              dossierId={contextDossierId ?? undefined}
              docType="invoice"
              onCancel={closeFollowUp}
            />
          </div>
        </div>
      ) : null}
    </>
  );
}
